import { Box, Button, Stack, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { FC, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetPartnerQuery } from "src/shared/api/partner";
import {
  LoadingButton,
  ModalComponent,
  EConfirmModalType,
} from "src/shared/components";
import { modalText } from "src/shared/constants/form.constants";
import { isApiError, showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import {
  usePatchNdaScanMutation,
  usePostLegalEntityCertificateMutation,
  usePostNdaScanMutation,
} from "../model/api/partners";
import { postPartnerFormDefaultValues } from "../model/constants";
import { getPartnerFormValues } from "../model/helpers/helpers";
import { useUpdatePartner } from "../model/hooks/useUpdatePartner";
import {
  IEditPartnerFormValues,
  INdaScanPostDTO,
} from "../model/types/partner";
import { Activity } from "./partnerForm/Activity";
import { BankDetails } from "./partnerForm/BankDetails";
import { ClientDescription } from "./partnerForm/ClientDescription";
import { ContactsShort } from "./partnerForm/ContactsShort";
import { GeneralInfo } from "./partnerForm/GeneralInfo";
import { RegistrationDetails } from "./partnerForm/RegistrationDetails";

export const EditPartner: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { partner_id } = useParams();

  const { data: partner, isLoading: isLoadingGetPartner } = useGetPartnerQuery(
    partner_id || skipToken,
  );

  const methods = useForm<IEditPartnerFormValues>({
    defaultValues: postPartnerFormDefaultValues,
    mode: "all",
    shouldFocusError: true,
  });
  const {
    reset,
    handleSubmit,
    getFieldState,
    setError,
    formState: { isDirty },
    watch,
  } = methods;
  const { isLoading, updatePartner } = useUpdatePartner({
    partner_id: partner_id || "",
    setError,
  });
  const [postNdaScan, { isLoading: postNdaScanIsLoading }] =
    usePostNdaScanMutation();
  const [patchNdaScan, { isLoading: patchNdaScanIsLoading }] =
    usePatchNdaScanMutation();
  const [
    postLegalEntityCertificate,
    { isLoading: postLegalEntityCertificateIsLoading },
  ] = usePostLegalEntityCertificateMutation();
  const { modal, showModal } = useConfirmModal({
    onResolve: onClose,
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });

  const handleSave = async (values: IEditPartnerFormValues) => {
    try {
      const {
        nda_note,
        nda_file,
        is_nda_signed,
        state_registration_certificate,
      } = values.partner;

      const shouldUpdateLicense =
        getFieldState("license").isDirty && !!partner?.license?.id;
      const shouldCreateLicense =
        getFieldState("license").isDirty && !partner?.license;
      const shouldUpdateBankAccount =
        getFieldState("bank_account").isDirty && !!partner?.bank_account?.id;
      const shouldCreateBankAccount =
        getFieldState("bank_account").isDirty && !partner?.bank_account;
      const shouldUpdatePartner = getFieldState("partner").isDirty;

      const isNdaInfoDirty =
        getFieldState("partner.is_nda_signed").isDirty ||
        getFieldState("partner.nda_note").isDirty;

      const isNdaFileDirty = getFieldState("partner.nda_file").isDirty;
      const isNewLegalEntityCertificateDirty = getFieldState(
        "partner.state_registration_certificate",
      ).isDirty;
      const isNewNdaFile =
        isNdaFileDirty && typeof watch("partner.nda_file") !== "string";
      const isNewLegalEntityCertificate =
        isNewLegalEntityCertificateDirty &&
        typeof watch("partner.state_registration_certificate") !== "string";

      const updateResponse = await updatePartner({
        shouldUpdateBankAccount,
        shouldCreateBankAccount,
        shouldUpdateLicense,
        shouldCreateLicense,
        shouldUpdatePartner,
        data: values,
      });

      if (partner_id) {
        if (isNewNdaFile) {
          await postNdaScan({
            partnerId: Number(partner_id),
            file: nda_file!,
            note: nda_note || "",
            is_nda_signed: !!is_nda_signed,
          }).unwrap();
        } else if (isNdaInfoDirty) {
          const body: Partial<INdaScanPostDTO> = {
            partnerId: Number(partner_id),
            is_nda_signed: values.partner.is_nda_signed,
          };

          if (values.partner.nda_note) {
            body.note = values.partner.nda_note;
          }

          await patchNdaScan(body).unwrap();
        } else if (isNewLegalEntityCertificate) {
          try {
            await postLegalEntityCertificate({
              partnerId: values.partner.id!,
              file: state_registration_certificate!,
            });
          } catch (error) {
            showApiError(error);

            enqueueSnackbar(
              "Не удалось прикрепить Свидетельство ЮЛ. Попробуйте позже",
              {
                variant: "warning",
              },
            );
          }
        }
      }
      if (!updateResponse.length) {
        onClose();

        return;
      }
      enqueueSnackbar("Партнер успешно обновлен", {
        variant: "success",
        handleClose: closeSnackbar,
      });
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        if (e.data.errors["registration_number"]) {
          setError(
            "partner.registration_number",
            {
              message: e.data.errors["registration_number"].join("\r\n"),
              type: "custom",
            },
            { shouldFocus: true },
          );
        }
      }
      showApiError(e);
    }
  };

  useEffect(() => {
    if (partner) {
      reset(getPartnerFormValues(partner), { keepDirty: true });
    }
  }, [partner, reset]);

  return (
    <ModalComponent
      width={() => ({ xs: "100%", sm: "95vw", xxl: 1520 })}
      disableBodyPadding
      title={partner ? partner.name : "Edit partner"}
      onClose={isDirty ? showModal : onClose}
      open
      isLoading={isLoadingGetPartner}
      modalFooter={
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button variant="outlined" onClick={isDirty ? showModal : onClose}>
            Отменить
          </Button>
          <LoadingButton
            isLoading={
              isLoading ||
              postNdaScanIsLoading ||
              patchNdaScanIsLoading ||
              postLegalEntityCertificateIsLoading
            }
            variant="contained"
            onClick={handleSubmit(handleSave)}
          >
            Сохранить
          </LoadingButton>
        </Box>
      }
    >
      {modal}
      <Box component="form" onSubmit={handleSubmit(handleSave)}>
        <FormProvider {...methods}>
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.grey[100],
              px: 4,
              py: 4,
            }}
          >
            <Stack direction="column" spacing={4}>
              <GeneralInfo />
              <RegistrationDetails isEdit />
              <BankDetails />
              <ClientDescription />
              <ContactsShort />
              <Activity />
            </Stack>
          </Box>
        </FormProvider>
      </Box>
    </ModalComponent>
  );
};
